import React, { useState, useEffect } from 'react';
import { FIELD_GROUPS } from '../constants/fieldDefs';
import { useCallStore } from '../store/useCallStore';

interface Props {
  onClose: () => void;
  onApply: () => void;
}

export default function FieldPicker({ onClose, onApply }: Props) {
  const { filter, setFilter } = useCallStore();
  const [pending, setPending] = useState<Set<string>>(new Set(filter.fields));
  const [search, setSearch] = useState('');
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set(['session']));

  useEffect(() => {
    setPending(new Set(filter.fields));
  }, [filter.fields]);

  function toggle(key: string) {
    setPending((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  function toggleGroup(groupId: string, keys: string[]) {
    const allSelected = keys.every((k) => pending.has(k));
    setPending((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        keys.forEach((k) => next.delete(k));
      } else {
        keys.forEach((k) => next.add(k));
      }
      return next;
    });
  }

  function toggleGroupOpen(id: string) {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function apply() {
    const fields = Array.from(pending);
    setFilter({ fields: fields.length > 0 ? fields : filter.fields });
    onApply();
    onClose();
  }

  const lowerSearch = search.toLowerCase();

  const filteredGroups = FIELD_GROUPS.map((group) => ({
    ...group,
    fields: search
      ? group.fields.filter(
          (f) =>
            f.label.toLowerCase().includes(lowerSearch) ||
            f.key.toLowerCase().includes(lowerSearch)
        )
      : group.fields,
  })).filter((g) => g.fields.length > 0);

  return (
    <>
      <div
        className="modal show d-block"
        tabIndex={-1}
        style={{ zIndex: 1055 }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title fw-semibold">Felder konfigurieren</h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>

            <div className="modal-body p-0">
              {/* Search */}
              <div className="px-3 pt-3 pb-2 border-bottom">
                <input
                  type="search"
                  className="form-control form-control-sm"
                  placeholder="Feld suchen…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoFocus
                />
              </div>

              {/* Groups */}
              <div className="accordion accordion-flush" id="fieldAccordion">
                {filteredGroups.map((group) => {
                  const groupKeys = group.fields.map((f) => f.key);
                  const selectedCount = groupKeys.filter((k) => pending.has(k)).length;
                  const allSelected = selectedCount === groupKeys.length;
                  const isOpen = search ? true : openGroups.has(group.id);

                  return (
                    <div className="accordion-item" key={group.id}>
                      <h2 className="accordion-header">
                        <div className="d-flex align-items-center px-3 py-2 gap-2 border-bottom">
                          <button
                            className={`accordion-button py-1 px-0 bg-transparent shadow-none fw-semibold text-body flex-grow-1 ${isOpen ? '' : 'collapsed'}`}
                            type="button"
                            onClick={() => !search && toggleGroupOpen(group.id)}
                            style={{ fontSize: '0.9rem' }}
                          >
                            {group.label}
                            <span className="ms-2 badge bg-primary-subtle text-primary-emphasis" style={{ fontSize: '0.7rem' }}>
                              {selectedCount}/{groupKeys.length}
                            </span>
                          </button>
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-nowrap text-decoration-none"
                            style={{ fontSize: '0.75rem' }}
                            onClick={() => toggleGroup(group.id, groupKeys)}
                          >
                            {allSelected ? 'Keine' : 'Alle'}
                          </button>
                        </div>
                      </h2>

                      {isOpen && (
                        <div className="accordion-collapse">
                          <div className="accordion-body py-2 px-3">
                            <div className="row g-1">
                              {group.fields.map((field) => (
                                <div className="col-12 col-sm-6 col-md-4" key={field.key}>
                                  <div
                                    className={`form-check rounded px-2 py-1 mb-0 ${pending.has(field.key) ? 'bg-primary-subtle' : ''}`}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => toggle(field.key)}
                                  >
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`field-${field.key}`}
                                      checked={pending.has(field.key)}
                                      onChange={() => toggle(field.key)}
                                      onClick={(e) => e.stopPropagation()}
                                      style={{ cursor: 'pointer' }}
                                    />
                                    <label
                                      className="form-check-label w-100"
                                      htmlFor={`field-${field.key}`}
                                      style={{ fontSize: '0.8rem', cursor: 'pointer', userSelect: 'none' }}
                                    >
                                      {field.label}
                                    </label>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {filteredGroups.length === 0 && (
                  <p className="text-muted text-center py-4 mb-0">Kein Feld gefunden.</p>
                )}
              </div>
            </div>

            <div className="modal-footer justify-content-between">
              <span className="text-muted small">
                {pending.size} Feld{pending.size !== 1 ? 'er' : ''} ausgewählt
              </span>
              <div className="d-flex gap-2">
                <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
                  Abbrechen
                </button>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={apply}
                  disabled={pending.size === 0}
                >
                  Übernehmen &amp; neu laden
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop show" style={{ zIndex: 1050 }} onClick={onClose} />
    </>
  );
}
